import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Send, Loader2, Bot } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const MD = {
  p: ({ node, ...p }) => <p className="mb-2 last:mb-0" {...p} />,
  ul: ({ node, ...p }) => <ul className="list-disc pl-4 space-y-1 my-2" {...p} />,
  ol: ({ node, ...p }) => <ol className="list-decimal pl-4 space-y-1 my-2" {...p} />,
  li: ({ node, ...p }) => <li className="leading-snug" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-semibold" {...p} />,
  a: ({ node, ...p }) => <a target="_blank" rel="noopener noreferrer" className="underline decoration-current/40 hover:decoration-current" {...p} />,
  h1: ({ node, ...p }) => <p className="font-semibold mb-1" {...p} />,
  h2: ({ node, ...p }) => <p className="font-semibold mb-1" {...p} />,
  h3: ({ node, ...p }) => <p className="font-semibold mb-1" {...p} />,
  code: ({ node, ...p }) => <code className="bg-black/5 px-1 rounded text-[0.85em]" {...p} />,
};

export default function BotChat() {
  const { publicKey } = useParams();
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const [cfg, setCfg] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showLead, setShowLead] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", message: "" });
  const [leadBusy, setLeadBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    api.get(`/bots/${publicKey}/config`)
      .then((r) => {
        setCfg(r.data);
        if (r.data.greeting) setMessages([{ role: "assistant", content: r.data.greeting }]);
      })
      .catch(() => setNotFound(true));
  }, [publicKey]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/bots/${publicKey}/chat`, { content: text, session_id: sessionId });
      if (data.session_id) setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.offer_lead && !leadSent) setShowLead(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Sorry, something went wrong. Please try again.";
      setMessages((m) => [...m, { role: "assistant", content: msg }]);
    } finally { setBusy(false); }
  };

  const sendLead = async () => {
    if (!lead.name.trim() || !lead.email.includes("@")) return;
    setLeadBusy(true);
    try {
      await api.post(`/bots/${publicKey}/lead`, { ...lead, session_id: sessionId });
      setLeadSent(true);
      setShowLead(false);
      setMessages((m) => [...m, { role: "assistant", content: `Thanks, ${lead.name.trim()}! We've got your details and the team will be in touch soon.` }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't send that — please check your details.";
      setMessages((m) => [...m, { role: "assistant", content: msg }]);
    } finally { setLeadBusy(false); }
  };

  const color = cfg?.brand_color || "#C9A227";

  if (notFound) {
    return <div className="min-h-screen bg-white flex items-center justify-center text-gray-500 text-sm">This assistant isn't available.</div>;
  }
  if (!cfg) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="animate-spin text-gray-400" /></div>;
  }

  return (
    <div className={`${embed ? "" : "min-h-screen"} bg-white flex flex-col`} style={{ height: embed ? "100vh" : undefined }} data-testid="bot-chat-page">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 text-white shrink-0" style={{ backgroundColor: color }}>
        <span className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"><Bot size={18} /></span>
        <div>
          <div className="font-semibold leading-tight">{cfg.name}</div>
          {cfg.client_name && <div className="text-[11px] opacity-90">{cfg.client_name}</div>}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed rounded-2xl ${m.role === "user" ? "text-white rounded-br-sm whitespace-pre-wrap" : "bg-white border border-gray-200 text-gray-800 rounded-bl-sm"}`}
              style={m.role === "user" ? { backgroundColor: color } : undefined}>
              {m.role === "user" ? m.content : <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{m.content}</ReactMarkdown>}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start"><div className="bg-white border border-gray-200 text-gray-500 rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> typing…</div></div>
        )}
        {showLead && !leadSent && (
          <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-2.5" data-testid="bot-lead-form">
            <div className="text-sm font-semibold text-gray-800">Leave your details</div>
            <p className="text-xs text-gray-500 -mt-1">Share your name and email and the team will follow up.</p>
            <input value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Your name" data-testid="bot-lead-name"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 outline-none focus:border-gray-400" />
            <input value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="Email address" type="email" data-testid="bot-lead-email"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 outline-none focus:border-gray-400" />
            <textarea value={lead.message} onChange={(e) => setLead({ ...lead, message: e.target.value })} placeholder="What can we help with? (optional)" rows={2} data-testid="bot-lead-message"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 outline-none focus:border-gray-400" />
            <div className="flex items-center gap-2">
              <button onClick={sendLead} disabled={leadBusy || !lead.name.trim() || !lead.email.includes("@")} data-testid="bot-lead-submit"
                className="text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-40" style={{ backgroundColor: color }}>
                {leadBusy ? "Sending…" : "Send"}
              </button>
              <button onClick={() => setShowLead(false)} className="text-xs text-gray-400 hover:text-gray-600">Not now</button>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 p-3 bg-white shrink-0">
        <div className="flex items-center gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Type your message…" data-testid="bot-input"
            className="flex-1 border border-gray-300 rounded-full px-4 py-2.5 text-sm text-gray-800 outline-none focus:border-gray-400" />
          <button onClick={send} disabled={busy || !input.trim()} data-testid="bot-send"
            className="w-10 h-10 rounded-full flex items-center justify-center text-white disabled:opacity-40" style={{ backgroundColor: color }}>
            <Send size={16} />
          </button>
        </div>
        <div className="text-center text-[10px] text-gray-400 mt-2">
          {!leadSent && (
            <button onClick={() => setShowLead(true)} data-testid="bot-leave-details" className="text-gray-400 hover:text-gray-600 underline mr-2">Leave your details</button>
          )}
          Powered by ATF WebWorks
        </div>
      </div>
    </div>
  );
}
